import { create } from 'zustand';
import { useEffect } from 'react';

type Kind = 'success' | 'error' | 'info';

interface ToastState {
  kind: Kind;
  message: string;
  // Auto-dismiss timestamp; null = manual dismiss only.
  expiresAt: number | null;
}

interface ToastStore {
  current: ToastState | null;
  show: (kind: Kind, message: string, ttlMs?: number) => void;
  dismiss: () => void;
}

/**
 * Lightweight global toast. Drop-in replacement for `alert(err)` calls — keeps
 * the dark-themed design instead of native browser dialogs that look out of
 * place in the admin UI.
 *
 *   useToast.getState().show('success', '已生成 50 个兑换码');
 *   useToast.getState().show('error', err.message);
 */
export const useToast = create<ToastStore>((set) => ({
  current: null,
  show: (kind, message, ttlMs = 4000) =>
    set({ current: { kind, message, expiresAt: ttlMs > 0 ? Date.now() + ttlMs : null } }),
  dismiss: () => set({ current: null }),
}));

/** Mount once near the app root (e.g. inside <Layout>) to render the toast. */
export function ToastViewport(): JSX.Element | null {
  const current = useToast((s) => s.current);
  const dismiss = useToast((s) => s.dismiss);

  useEffect(() => {
    if (current?.expiresAt === null || current?.expiresAt === undefined) return undefined;
    const ms = current.expiresAt - Date.now();
    if (ms <= 0) {
      dismiss();
      return undefined;
    }
    const id = setTimeout(dismiss, ms);
    return () => clearTimeout(id);
  }, [current, dismiss]);

  if (current === null) return null;

  const palette = {
    success: 'bg-felt-700/30 border-felt-500/50 text-felt-200',
    error: 'bg-rose-900/40 border-rose-700/60 text-rose-200',
    info: 'bg-shark-900/40 border-shark-700/60 text-shark-200',
  }[current.kind];

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-md">
      <div className={`px-4 py-3 rounded-lg border shadow-lg ${palette} flex items-start gap-3`}>
        <span className="flex-1 text-sm">{current.message}</span>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs opacity-60 hover:opacity-100"
          aria-label="关闭"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
